/**
 * The store talks to Tebex's Headless API straight from the player's browser (docs/STORE.md):
 * the catalogue, and the basket the checkout pays. Only the public token comes from the
 * panel; a basket made here carries the player's own address, which Tebex's checks read.
 */

export const HEADLESS = 'https://headless.tebex.io/api'

export interface Webstore {
  id: number
  name: string
  description: string
  currency: string
  lang: string
  logo: string | null
  webstore_url: string
}

export interface Media {
  type: 'video' | 'image'
  name: string | null
  url: string
  featured: boolean
}

/** single, subscription, or both: then the player picks. */
export type PackageType = 'single' | 'subscription' | 'both'

export interface Package {
  id: number
  name: string
  description: string
  type: PackageType
  disable_quantity: boolean
  currency: string
  category: { id: number; name: string }
  base_price: number
  sales_tax: number
  total_price: number
  discount: number
  image: string | null
  media?: Media[]
  order?: number
  slug?: string | null
  user_limit?: number | null
}

export interface Category {
  id: number
  name: string
  description: string
  parent: Category | null
  order: number
  packages: Package[] | null
  display_type: 'grid' | 'list'
  slug: string | null
}

export interface BasketPackage {
  id: number
  name: string
  description: string
  image: string | null
  type?: 'single' | 'subscription'
  in_basket: { quantity: number; price: number }
}

export interface Basket {
  ident: string
  complete: boolean
  username: string | null
  username_id: number | null
  base_price: number
  sales_tax: number
  total_price: number
  currency: string
  packages: BasketPackage[]
  coupons: { coupon_code: string }[]
  giftcards: { card_number: string }[]
  creator_code: string
  links: { checkout: string }
}

export interface AuthOption {
  name: string
  url: string
}

/** What Tebex answers when it refuses: `detail` is a sentence a player can read. */
export class StoreError extends Error {
  status: number
  constructor(status: number, detail: string) {
    super(detail)
    this.status = status
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${HEADLESS}${path}`, {
      method,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new StoreError(0, 'Lo store non risponde. Controlla la connessione e riprova.')
  }
  const text = await response.text()
  let json: unknown
  try {
    json = text ? JSON.parse(text) : undefined
  } catch {
    json = undefined
  }
  if (!response.ok) {
    const problem = json as { detail?: string; message?: string; title?: string } | undefined
    throw new StoreError(
      response.status,
      problem?.detail || problem?.message || problem?.title || 'Qualcosa è andato storto.',
    )
  }
  return json as T
}

const data = <T>(answer: Promise<{ data: T }>) => answer.then((value) => value.data)

/** The panel's word on which store this is: null while it is closed. */
export async function readToken(): Promise<string | null> {
  const response = await fetch('/api/public/tebex/store', {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new StoreError(response.status, 'Lo store non risponde.')
  return ((await response.json()) as { token: string | null }).token
}

export function headless(token: string) {
  const account = `/accounts/${encodeURIComponent(token)}`
  const basket = (ident: string) => `/baskets/${encodeURIComponent(ident)}`
  return {
    webstore: () => data(call<{ data: Webstore }>('GET', account)),
    categories: (ident?: string | null) =>
      data(
        call<{ data: Category[] }>(
          'GET',
          `${account}/categories?includePackages=1${ident ? `&basketIdent=${encodeURIComponent(ident)}` : ''}`,
        ),
      ),
    basket: (ident: string) => data(call<{ data: Basket }>('GET', `${account}${basket(ident)}`)),
    createBasket: (complete_url: string, cancel_url: string) =>
      data(
        call<{ data: Basket }>('POST', `${account}/baskets`, {
          complete_url,
          cancel_url,
          complete_auto_redirect: true,
        }),
      ),
    /** Where the player signs in with Cfx.re, then comes back to `returnUrl`. */
    auth: (ident: string, returnUrl: string) =>
      call<AuthOption[] | { data: AuthOption[] }>(
        'GET',
        `${account}${basket(ident)}/auth?returnUrl=${encodeURIComponent(returnUrl)}`,
      ).then((answer) => (Array.isArray(answer) ? answer : answer.data)),
    add: (ident: string, package_id: number, type: 'single' | 'subscription', quantity = 1) =>
      data(
        call<{ data: Basket }>('POST', `${basket(ident)}/packages`, { package_id, quantity, type }),
      ),
    remove: (ident: string, package_id: number) =>
      data(call<{ data: Basket }>('POST', `${basket(ident)}/packages/remove`, { package_id })),
    quantity: (ident: string, package_id: number, quantity: number) =>
      data(call<{ data: Basket }>('PUT', `${basket(ident)}/packages/${package_id}`, { quantity })),
    coupon: (ident: string, coupon_code: string) =>
      call('POST', `${account}${basket(ident)}/coupons`, { coupon_code }),
    dropCoupon: (ident: string, coupon_code: string) =>
      call('POST', `${account}${basket(ident)}/coupons/remove`, { coupon_code }),
    creator: (ident: string, creator_code: string) =>
      call('POST', `${account}${basket(ident)}/creator-codes`, { creator_code }),
    dropCreator: (ident: string) =>
      call('POST', `${account}${basket(ident)}/creator-codes/remove`, {}),
  }
}

export type Headless = ReturnType<typeof headless>

export function price(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export const DISCORD = 'https://discord.gg/unity-roleplay-608661985747271691'
export const SITE = 'https://unityrp.it/'
export const PLAY = 'https://play.unityrp.it/'
export const STATUS = 'https://status.unityrp.it/'
